<template>
  <Modal
    :value="value"
    @input="$emit('input', $event)"
    maxWidth="600px"
    dataTestId="claim-form-selection-modal"
    contentClass="pa-8"
  >
    <template #header>
      <Typography variant="h-2" class="claim-form-modal-title"
        >Which form applies to your sale?</Typography
      >
    </template>
    <Typography variant="body-1" class="claim-form-modal-subtitle"
      >Each form covers one or more programs.</Typography
    >
    <div v-if="isLoading" class="d-flex justify-center pa-4">
      <v-progress-circular indeterminate color="brandPrimary" />
    </div>
    <div v-else-if="error" class="pa-4 text-center">
      <Typography variant="body-1" class="error--text">{{ error }}</Typography>
    </div>
    <div v-else-if="forms.length === 0" class="pa-4 text-center">
      <Typography variant="body-2">No claim forms available.</Typography>
    </div>
    <div v-else class="claim-form-list">
      <div
        v-for="(form, index) in forms"
        :key="form.formId"
        :data-testid="'claim-form-item-' + index"
        class="claim-form-card"
        role="button"
        tabindex="0"
        @click="$emit('select', form)"
        @keydown.enter="$emit('select', form)"
        @keydown.space.prevent="$emit('select', form)"
      >
        <div class="claim-form-card-text">
          <Typography variant="h-5" class="claim-form-name">{{ form.formName }}</Typography>
          <div class="claim-form-programs">
            <Typography variant="subtitle-1" class="claim-form-programs-label"
              >Programs:</Typography
            >
            <Typography v-for="program in form.programs" :key="program.programId" variant="body-1">
              {{ program.programName }}
            </Typography>
          </div>
        </div>
        <Icon icon="mdi-chevron-right" color="rgba(0, 0, 0, 0.6)" />
      </div>
    </div>
  </Modal>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Modal from "@/components/generic/Modal.vue";
import Icon from "@/components/generic/Icon.vue";
import ProgramClaimServiceV3 from "@/services/ProgramClaimServiceV3";

export default {
  name: "FormSelectionModal",
  components: {
    Typography,
    Modal,
    Icon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      claimService: null,
      forms: [],
      isLoading: false,
      error: "",
    };
  },
  created() {
    this.claimService = new ProgramClaimServiceV3();
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.fetchForms();
      }
    },
  },
  methods: {
    async fetchForms() {
      this.isLoading = true;
      this.error = "";
      this.forms = [];
      try {
        this.forms = await this.claimService.getEligibleForms();
      } catch (e) {
        this.error = "Unable to load claim forms. Please try again.";
        this.forms = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.claim-form-modal-title {
  flex: 1 1 0%;
  color: rgba(0, 0, 0, 0.87);
  text-align: left;
}

.claim-form-modal-subtitle {
  margin-top: 4px;
  margin-bottom: 24px;
  color: rgba(0, 0, 0, 0.87);
  text-align: left;
}

.claim-form-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.claim-form-card {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.claim-form-card:hover,
.claim-form-card:focus-visible {
  background-color: rgba(0, 0, 0, 0.09);
}

.claim-form-card-text {
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  color: rgba(0, 0, 0, 0.87);
}

.claim-form-name {
  width: 100%;
}

.claim-form-programs {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
}

.claim-form-programs-label {
  color: rgba(0, 0, 0, 0.6);
}
</style>
